import React, { FC, useEffect, useContext } from 'react';
import _ from 'lodash';
import { Skeleton } from 'antd';
import {
    formatNumWithKMB,
    formatNumWithKMBAlt,
    calculateFontSize,
    getConfig,
    getMaxBound,
    getMinBound,
    removeFunctionFromProps,
} from 'utils/helperMethods';

const offsetXDistanceVar = -1 * calculateFontSize(window.innerWidth, 7);
import { UserContext } from 'contexts/ConfigContext';
import useTrackTabChange from 'hooks/general/useTrackTabChange';
const G2 = require('@antv/g2');

//antv g2 doesnt render these string in tooltip and returns empty string,
//inorder to avoid them from displaying empty string, displaying alternative name
const replacementKeyMapping: any = {
    '<NA>': 'NA',
    '<na>': 'na',
};

//ADD ALL THE REQUIRED PROPS AT TOP
interface Props {
    fields: Array<string>;
    dataset: Array<any>;
    chartContainer: string;
    height: string | number;
    loading: boolean;
    colorKey: string;
    lineChartParams?: any; //add this key if line chart is present
    showLoader?: boolean;
    yAxisConfig?: boolean | object;
    xAxisConfig?: boolean | object;
    chartConfig?: object;
    yAxisLabelFormatter?: (val: any) => any;
    xAxisLabelFormatter?: (val: any, item: any, index: number) => any;
    tooltipConfig?: object;
    YGrid?: boolean;
    XGrid?: boolean;
    padding?: [number, number, number, number];
    width?: number;
    barWidth?: number;
    yAxisTitle?: string;
    xAxisTitle?: string;
    error?: any;
    additionalStyle?: any;
    minBound?: number;
    maxBound?: number;
    showLegend?: boolean;
    showErrorBox?: boolean;
    transpose?: boolean;
    Yline?: boolean;
    Xline?: boolean;
    skipIntervalLabel?: number;
    groupedChartColor?: [string, string];
    intervalLabelKey?: string;
    intervalStyle?: object;
    disableIntervalLabel?: boolean;
    toolTipFields?: string;
    tooltipFormatter?: (xAxis: any, val: any, attributeDesc?: any) => any;
    addPercentageInIntervalLabel?: boolean;
    showStackedItemLabel?: boolean;
    extras?: any;
    intervalFormatter?: (val: any) => any;
    areaChartParams?: any;
    totalKey?: string;
    currency?: string;
    xLabelFontSize?: number;
    disableTotalLabelYOffset?: boolean;
    positionOverrideKey?: string;
}

const StackedBarChart: FC<Props> = ({
    yAxisConfig = true,
    xAxisConfig = true,
    chartConfig = {},
    height = 400,
    width,
    fields,
    xAxisTitle = '',
    yAxisTitle = '',
    colorKey = 'color',
    additionalStyle,
    dataset,
    lineChartParams = null,
    chartContainer,
    tooltipConfig = {},
    loading,
    padding = [0, 0, 0, 0],
    error,
    barWidth = 20,
    YGrid = true,
    XGrid = false,
    Yline = false,
    Xline = true,
    showLoader = true,
    showErrorBox = true,
    minBound,
    showLegend = false,
    yAxisLabelFormatter,
    xAxisLabelFormatter,
    tooltipFormatter = (xAxis, val, attributeDesc?) => {
        return {
            name: xAxis,
            value: addPercentageInIntervalLabel ? formatNumWithKMB(val) : formatNumWithKMBAlt(val, 2, 2),
        };
    },
    intervalFormatter = (val: any) => {
        return addPercentageInIntervalLabel ? formatNumWithKMB(val) + '%' : formatNumWithKMBAlt(val, 2, 2);
    },
    maxBound,
    transpose,
    skipIntervalLabel = 1,
    groupedChartColor = ['', ''],
    intervalStyle = {},
    intervalLabelKey = 'totalSales',
    disableIntervalLabel = false,
    addPercentageInIntervalLabel = false,
    showStackedItemLabel = false,
    extras = {},
    areaChartParams,
    totalKey = '',
    currency = '',
    xLabelFontSize,
    disableTotalLabelYOffset = false,
    toolTipFields,
    positionOverrideKey,
}) => {
    // if (!loading && error && dataset?.length === 0 && showErrorBox) {
    //     return <ErrorBox msg={String(error)} />;
    // }
    const isWindowTabActive = useTrackTabChange();
    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const decimalForPercentage = commonSetting?.decimalForPercentage ?? 2;
    const decimalForNonPercentage = commonSetting?.decimalForNonPercentage ?? 2;
    const { graphFontSize, graphLabelColor, graphFontSizeAlt, graphLabelColorAlt, graphFontFamily } = commonSetting;

    useEffect(() => {
        if (!window) return;
        const defaultTooltipConfig = {
            showMarkers: false,
            shared: true,
            showCrosshairs: false,
            follow: true,
        };

        const min = minBound ? minBound : getMinBound(dataset, fields);
        const max = maxBound ? maxBound : getMaxBound(dataset, fields);
        const chart = new G2.Chart({
            container: chartContainer,
            height: height,
            width: width,
            autoFit: width ? false : true,
            appendPadding: padding,
        });
        chart.data(dataset);

        chart.scale(fields[1], {
            nice: true,
            min: min,
            max: max,
            ...chartConfig,
        });
        chart.scale(intervalLabelKey, {
            nice: true,
            min: min,
            max: max,
            ...chartConfig,
        });
        if (positionOverrideKey) {
            chart.scale(positionOverrideKey, { nice: true, min: min, max: max, ...chartConfig });
            chart.axis(positionOverrideKey, false);
        }
        //line chart
        if (lineChartParams) {
            lineChartParams?.fields?.forEach((field: string) => {
                chart.scale(field, {
                    nice: true,
                    min: lineChartParams.minBound,
                    max: lineChartParams.maxBound,
                });
            });
        }
        //area chart
        if (areaChartParams) {
            chart.scale(fields?.[5], {
                nice: true,
                min: areaChartParams.minBound,
                max: areaChartParams.maxBound,
                ...chartConfig,
            });
            chart.axis(fields?.[5], false);
        }
        //creating this axis for labels
        chart.axis(intervalLabelKey, false);
        if (yAxisConfig) {
            chart.axis(fields[1], {
                style: {
                    textAlign: 'center',
                },
                label: !yAxisLabelFormatter
                    ? null
                    : {
                          formatter: yAxisLabelFormatter,
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
                line: Yline ? {} : null,
                grid: YGrid
                    ? {
                          line: {
                              style: {
                                  lineDash: [3, 3],
                              },
                          },
                      }
                    : null,
                title: !yAxisTitle
                    ? null
                    : {
                          text: yAxisTitle,
                          position: 'middle',
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
                position: 'left',
            });
        }
        if (!yAxisConfig) chart.axis(fields[1], false);
        if (!xAxisConfig) chart.axis(fields[0], false);
        if (xAxisConfig) {
            chart.axis(fields[0], {
                label: !xAxisLabelFormatter
                    ? null
                    : {
                          autoHide: false,
                          autoEllipsis: xLabelFontSize ? true : false,
                          formatter: xAxisLabelFormatter,
                          style: {
                              fontSize: xLabelFontSize ? xLabelFontSize : graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
                tickLine: false,
                line: Xline ? {} : null,
                grid: XGrid
                    ? {
                          line: {
                              style: {
                                  lineDash: [3, 3],
                              },
                          },
                      }
                    : null,
                title: !xAxisTitle
                    ? null
                    : {
                          text: xAxisTitle,
                          position: 'start',
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
            });
        }
        //chart for label purpose only
        if (!disableIntervalLabel) {
            // positionOverrideKey: positions the invisible bar at the visual bar tip (e.g. barTip)
            const posKey = positionOverrideKey || intervalLabelKey;
            const labelFields = fields[4] ? `${intervalLabelKey}*${fields[4]}` : `${intervalLabelKey}`;
            chart
                .interval()
                .position(`${fields[0]}*${posKey}`)
                .size(0)
                .label(labelFields, (val: any, index: any) => {
                    if (val === undefined || val === null) return;
                    if (!fields[4] || index % skipIntervalLabel === 0) {
                        const pos = val > 0 ? (transpose ? 'end' : 'top') : transpose ? 'start' : 'bottom';
                        const formattedValue = val === 0 ? '' : intervalFormatter(val);
                        return {
                            offsetY: disableTotalLabelYOffset ? 0 : val < 0 ? -10 : 10,
                            offsetX:
                                disableTotalLabelYOffset && transpose
                                    ? val < 0
                                        ? offsetXDistanceVar * String(formattedValue).length
                                        : -8
                                    : 0,
                            position: pos,
                            content: formattedValue || null,
                            style: {
                                fontSize: graphFontSizeAlt,
                                fill: graphLabelColorAlt,
                                fontFamily: graphFontFamily,
                            },
                        };
                    }
                })
                .tooltip(false)
                .style({
                    stroke: 'white',
                    lineWidth: 1,
                });
        }

        //AREA CHART
        if (areaChartParams) {
            chart
                .area()
                .position(`${fields?.[0]}*${fields?.[5]}`)
                .color('#d0d0d7')
                .style({ fillOpacity: 1 })
                .tooltip(`${fields[5]}`, (val: any) => {
                    return {
                        name: areaChartParams?.tooltipLabel,
                        value: formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage),
                    };
                });
        }

        //STACK CHART
        chart
            .interval()
            .adjust([
                {
                    type: 'stack',
                    //dodgeBy: 'category',
                    marginRatio: 0,
                },
            ])
            .position(`${fields[0]}*${fields[1]}`)
            .color(`color`, (color: any) => {
                return color;
            })
            .size(barWidth)
            .tooltip(toolTipFields || `${fields[2]}*${fields[1]}`, (xAxis: any, val: any, attributeDesc: any) => {
                let temp = xAxis;
                if (temp in replacementKeyMapping) temp = replacementKeyMapping[temp];
                return tooltipFormatter(temp, val, attributeDesc);
            })
            .label(`${fields[1]}`, (val: number) => {
                // if (val < 2) {
                //     return null;
                // }
                if (!showStackedItemLabel) return null;
                return {
                    position: 'middle',
                    offset: 0,
                    // offsetX: -10,
                    content: (originData: any) => {
                        return formatNumWithKMB(originData.sales) + '%';
                    },
                    style: {
                        fontSize: graphFontSizeAlt,
                        fill: graphLabelColorAlt,
                        fontFamily: graphFontFamily,
                    },
                };
            })
            .style({
                stroke: 'white',
                lineWidth: 1,
            });

        if (transpose) chart.coordinate().transpose();

        //for line chart
        if (lineChartParams) {
            lineChartParams?.fields?.forEach((field: string, index: number) => {
                chart.axis(field, false);
                const { color, tooltipFormatter, lineColors = [] } = lineChartParams;
                chart
                    .line()
                    .size(2)
                    .position(`${fields[0]}*${field}`)
                    .color(lineColors[index] || color)
                    .label(`${field}`, (val: number) => {
                        if (lineChartParams?.disableIntervalLabel) return;
                        const pos = val > 0 ? (transpose ? 'end' : 'top') : transpose ? 'start' : 'bottom';
                        return {
                            position: pos,
                            content: val === 0 ? null : formatNumWithKMB(val),
                            style: {
                                fontSize: graphFontSizeAlt,
                                fill: graphLabelColorAlt,
                                fontFamily: graphFontFamily,
                            },
                        };
                    })
                    .tooltip(`${fields[0]}*${field}`, (xAxis: any, val: any) => {
                        return tooltipFormatter(xAxis, val, field);
                    });
                // .style({ ...intervalStyle });
            });
        }
        //just for tooltip

        chart.tooltip({
            ...defaultTooltipConfig,
            ...tooltipConfig,
            containerTpl:
                '<div class="g2-tooltip" style="min-width:250px;">' +
                '<div class="g2-tooltip-title" style="margin:10px 0;"></div>' +
                '<ul class="g2-tooltip-list"></ul></div>',
            customItems: (items: any[]) => {
                const duplicatedItems = [...items];
                const lastTooltipItems: any[] = [];
                //dont add for groupby
                if (items?.[0]?.data?.totalValueExceptBase) {
                    const totalExceptBaseObj = {
                        name: `Total ${totalKey}`,
                        value: `${currency}${formatNumWithKMBAlt(
                            items?.[0]?.data?.totalValueExceptBase,
                            decimalForNonPercentage,
                            decimalForNonPercentage,
                        )}`,
                        title: items[0]?.title,
                    };
                    duplicatedItems?.splice(1, 0, totalExceptBaseObj);
                }
                duplicatedItems.forEach((item, index) => {
                    if (item.name === 'YA Sales') {
                        lastTooltipItems.push(item);
                        duplicatedItems.splice(index, 1);
                    }
                });
                if (extras?.data && Object.keys(extras.data)?.length) {
                    const item = extras?.data?.[items[0]?.title];
                    let newItems: any[] = [];
                    if (item) {
                        newItems = Object.keys(item).map((elem) => {
                            const label = extras?.dataMapping?.[elem]?.['label'] || elem;
                            return {
                                name: label,
                                value: isNaN(item[elem])
                                    ? item[elem]
                                    : formatNumWithKMBAlt(item[elem], decimalForNonPercentage, decimalForNonPercentage),
                                title: items[0]?.title,
                            };
                        });
                    }
                    return [...newItems, ...duplicatedItems, ...lastTooltipItems];
                }
                return duplicatedItems;
                //     const filteredItems = items?.filter(item=> 'name' in item)
                //    return filteredItems
            },
        });

        if (!showLegend) chart.legend(false);
        else
            chart.legend({
                position: 'top',
                layout: 'horizontal',
            });

        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [dataset, width, groupedChartColor, isWindowTabActive, skipIntervalLabel]);

    return (
        <div className="">
            {showLoader && (
                <>
                    <Skeleton loading={loading} />
                    <Skeleton loading={loading} />
                </>
            )}
            <div style={{ opacity: loading ? 0.3 : 1, ...additionalStyle }} id={chartContainer} />
        </div>
    );
};

function arePropsEqual(prevProps: any, nextProps: any) {
    const [clonePrev, cloneNext] = removeFunctionFromProps({ ...prevProps }, { ...nextProps }); //remove function type from props for referential equality
    return _.isEqual(clonePrev, cloneNext);
}

export default React.memo(StackedBarChart, arePropsEqual);
